/* eslint-disable no-nested-ternary */
import React, {
  createContext,
  Dispatch,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Socket, connect as ioConnect } from 'socket.io-client';
import { useAuth } from './auth';
import OrdersService from '../services/OrdersService';
import ListOrdersServiceResponse from '../interfaces/IListOrders';
import { newDebounce } from '../utils/debounce';

interface AuthProviderProps {
  children: ReactNode;
}

interface MonitorContextData {
  listOrders: ListOrdersServiceResponse | null;
  setListOrders: Dispatch<
    React.SetStateAction<ListOrdersServiceResponse | null>
  >;
  isLoading: boolean;
  filter: {
    text: string;
    insternship: number[];
  };
  setFilter: React.Dispatch<
    React.SetStateAction<{
      text: string;
      insternship: number[];
    }>
  >;
  handleChangePage: () => void;
  emAguardo: boolean;
  checkEmAguardo: () => void;
  setEmAguardo: React.Dispatch<React.SetStateAction<boolean>>;
}

const MonitorContext = createContext<MonitorContextData>(
  {} as MonitorContextData,
);

const useMonitor: () => MonitorContextData = () => {
  const context = useContext(MonitorContext);

  if (!context) {
    throw new Error('useMonitor must be used within an MonitorProvider');
  }

  return context;
};

const MonitorProvider: ({ children }: AuthProviderProps) => JSX.Element = ({
  children,
}: AuthProviderProps) => {
  const [
    listOrders,
    setListOrders,
  ] = useState<ListOrdersServiceResponse | null>(null);
  const [emAguardo, setEmAguardo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<{
    text: string;
    insternship: number[];
  }>({ text: '', insternship: [0] });

  const socketIORef = useRef<typeof Socket>();
  // Ref para acessar o filtro atual dentro do closure do socket
  const filterRef = useRef(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  const { caixa, user } = useAuth();

  const checkEmAguardo = useCallback(async () => {
    if (user.empresa_id && caixa) {
      const list = await OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        1,
        1,
        '0',
      );

      if (
        list.orders.some(
          (s_order) =>
            s_order.status === 0 ||
            (s_order.ifoodOrder && s_order.ifoodOrder.status === 'PLACED'),
        )
      ) {
        setEmAguardo(true);
      } else {
        setEmAguardo(false);
      }
    }
  }, [user, caixa]);

  const requestByFilter = (f_value: any, f_user: any, f_caixa: any): void => {
    OrdersService.listOrders(
      f_user.empresa_id,
      f_caixa.id,
      1,
      20,
      f_value.insternship.join(','),
      f_value.text,
    )
      .then((res) => {
        return setListOrders(res);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const pedidoPorFiltro: (
    d_value: any,
    d_user: any,
    d_caixa: any,
  ) => void = useCallback(
    newDebounce(
      (d_value: string, d_user: any, d_caixa: any): void =>
        requestByFilter(d_value, d_user, d_caixa),
      2000,
    ),
    [],
  );

  const handleChangePage = (): void => {
    if (listOrders) {
      setIsLoading(true);
      OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        listOrders.currentPage + 1,
        20,
        filter.insternship.join(','),
      )
        .then((res) => {
          setListOrders((oldState) => ({
            ...res,
            orders: [...oldState!.orders, ...res.orders].sort(
              (a: any, b: any) => b.id - a.id,
            ),
          }));
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (caixa.id) {
      setIsLoading(true);
      OrdersService.listOrders(
        user.empresa_id,
        caixa.id,
        1,
        20,
        filter.insternship.join(','),
      )
        .then((res) => {
          setListOrders(res);
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          setIsLoading(false);
        });

      if (socketIORef.current?.connected || !user) return;

      socketIORef.current = ioConnect(`${process.env.REACT_APP_SOCKET_URL}`, {
        query: { companyId: user.empresa_id },
      });

      socketIORef.current?.on(
        `company[${user.empresa_id}]`,
        (orderData: any) => {
          if (filterRef.current.insternship.includes(0)) {
            setListOrders((oldState) => {
              if (!oldState) return oldState;

              const { orders } = oldState;

              let foundOrderDataId = -1;

              if (orderData.ifoodOrder) {
                if (orderData.ifoodOrder.status === 'CANCELLED') {
                  return {
                    ...oldState!,
                    orders: orders.filter(
                      (order) =>
                        order.ifoodOrder?._id !== orderData.ifoodOrder._id,
                    ),
                  };
                }

                foundOrderDataId = orders.findIndex(
                  (order) => order.ifoodOrder?._id === orderData.ifoodOrder._id,
                );
              } else {
                foundOrderDataId = orders.findIndex(
                  (order) => order.id === orderData.id,
                );
              }

              if (foundOrderDataId > -1) {
                const statusMatchesFilter =
                  filterRef.current.insternship.includes(orderData.status);
                if (!statusMatchesFilter && !orderData.ifoodOrder) {
                  // Status não corresponde mais ao filtro atual → remove da lista
                  orders.splice(foundOrderDataId, 1);
                } else {
                  orders.splice(foundOrderDataId, 1, orderData);
                }
              } else if (orderData.ifoodOrder) {
                if (orderData.ifoodOrder.status === 'PLACED') {
                  orders.push(orderData);
                }
              } else {
                orders.push(orderData);
              }

              console.log('RECEBIDO');
              console.log(orderData);
              console.log('RETORNADO');
              console.log({ ...oldState!, orders });

              return { ...oldState!, orders };
            });
          }

          // Novo pedido aguardando confirmação → ativa o indicador
          if (
            orderData.status === 0 ||
            (orderData.ifoodOrder && orderData.ifoodOrder.status === 'PLACED')
          ) {
            setEmAguardo(true);
          } else {
            // Atualização de status → reavalia se ainda há pedidos em aguardo
            checkEmAguardo();
          }
        },
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caixa, user]);

  useEffect(() => {
    if (user?.id && caixa?.id) {
      setIsLoading(true);
      pedidoPorFiltro(filter, user, caixa);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.text]);

  useEffect(() => {
    if (user?.id && caixa?.id) {
      setIsLoading(true);
      requestByFilter(filter, user, caixa);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.insternship.join(',')]);

  return (
    <MonitorContext.Provider
      value={
        {
          listOrders,
          setListOrders,
          filter,
          setFilter,
          handleChangePage,
          emAguardo,
          setEmAguardo,
          isLoading,
          checkEmAguardo,
        } as MonitorContextData
      }
    >
      {children}
    </MonitorContext.Provider>
  );
};

export { MonitorProvider, useMonitor };
